// ============================================================
// src/shack/shackDatos.js — Datos del Shack Virtual
// ============================================================

import { db, storage } from '../firebase.js';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';

const refShack = (userId) => doc(db, 'shacks', userId);

function _shackPorDefecto(userId) {
  return {
    userId,
    entorno: 'real',
    version: 2,
    items: [],
    compartir: {
      ultimaActualizacion: null,
    },
  };
}

export async function obtenerShack(userId) {
  try {
    const snap = await getDoc(refShack(userId));
    if (snap.exists()) {
      const data = snap.data() || {};
      return {
        ..._shackPorDefecto(userId),
        ...data,
        items: Array.isArray(data.items) ? data.items : [],
      };
    }
    return _shackPorDefecto(userId);
  } catch (error) {
    console.error('[Shack] Error al obtener shack:', error);
    return _shackPorDefecto(userId);
  }
}

export async function guardarShack(userId, datos) {
  try {
    await setDoc(
      refShack(userId),
      {
        ...datos,
        version: 2,
        compartir: {
          ultimaActualizacion: new Date().toISOString(),
        },
      },
      { merge: true }
    );
    console.log('[Shack] Shack guardado.');
  } catch (error) {
    console.error('[Shack] Error al guardar shack:', error);
    throw error;
  }
}

// ── Certificados disponibles ──────────────────────────────────
//
// Los archivos están en la subcolección editions/{edicionId}/files
// con campo tipo='cert'. Se consulta cada subcolección directamente
// (una por edición) para evitar collectionGroup que requiere índice.

export async function cargarCertificadosDisponibles(userId, incluirOcultos = false) {
  try {
    // Paso 1: todas las ediciones del usuario
    const qEdiciones = query(
      collection(db, 'editions'),
      where('userId', '==', userId)
    );
    const snapEdiciones = await getDocs(qEdiciones);

    if (snapEdiciones.empty) {
      console.log('[Shack] El usuario no tiene ediciones.');
      return [];
    }

    // Paso 2: por cada edición, consultar su subcolección /files
    // en paralelo — sin collectionGroup, sin índice especial
    const promesas = snapEdiciones.docs.map(async (edicionDoc) => {
      const datosEdicion = edicionDoc.data() || {};

      const qFiles = query(
        collection(db, 'editions', edicionDoc.id, 'files'),
        where('tipo', '==', 'cert')
      );
      const snapFiles = await getDocs(qFiles);

      return snapFiles.docs
        .filter((fileDoc) => incluirOcultos || (fileDoc.data() || {}).shackVisible !== false)
        .map((fileDoc) => {
          const archivo = fileDoc.data() || {};
          return {
            tipo:        'certificado',
            shackKey:    `cert:${edicionDoc.id}:${fileDoc.id}`,
            edicionId:   edicionDoc.id,
            archivoId:   fileDoc.id,
            storageType: archivo.storageType || 'user_drive',
            fileId:      archivo.fileId  || archivo.driveId  || '',
            fileUrl:     archivo.fileUrl || archivo.driveUrl || '',
            concurso:    datosEdicion.concurso || '—',
            anio:        datosEdicion.año || datosEdicion.year || '—',
            modo:        datosEdicion.modo || '—',
            nombre:      archivo.nombre || archivo.name || 'Certificado',
          };
        });
    });

    const resultados = await Promise.all(promesas);
    const certificados = resultados.flat();

    console.log(`[Shack] ${certificados.length} certificado/s encontrado/s.`);
    return certificados;

  } catch (error) {
    console.error('[Shack] Error al cargar certificados:', error);
    return [];
  }
}

// ── Badge de certificados nuevos ──────────────────────────────

export async function contarCertificadosNuevos(userId) {
  try {
    const [disponibles, shack] = await Promise.all([
      cargarCertificadosDisponibles(userId),
      obtenerShack(userId),
    ]);
    const keysEnShack = new Set((shack.items || []).map((i) => i.shackKey));
    const nuevos = disponibles.filter((c) => !keysEnShack.has(c.shackKey));
    return nuevos.length;
  } catch (error) {
    console.error('[Shack] Error al contar certificados nuevos:', error);
    return 0;
  }
}

// ── Combinación de items ──────────────────────────────────────

export function combinarItemsShack(disponibles, guardados) {
  const guardadosMap = new Map(
    (guardados || []).map((g) => [g.shackKey, g])
  );

  const resultado = [];
  let indice = 0;

  for (const cert of disponibles) {
    const guardado = guardadosMap.get(cert.shackKey);

    if (guardado) {
      resultado.push({
        ...cert,
        ...guardado,
        tipo: 'certificado',
        shackKey: cert.shackKey,
        storageType: cert.storageType || guardado.storageType || 'user_drive',
      });
    } else {
      const col = indice % 3;
      const fila = Math.floor(indice / 3);

      resultado.push({
        ...cert,
        tipo: 'certificado',
        wall: 'trasera',
        u: 0.18 + (col * 0.24),
        v: 0.18 + (fila * 0.20),
        w: 0.30,
        h: 0.20,
        ratio: 1.5,
        frameTone: 0.60,
        zIndex: indice,
        visible: true,
      });

      indice += 1;
    }
  }

  // Conservar items especiales (mapas) aunque no estén en disponibles
  for (const g of guardados || []) {
    if (g.tipo === 'mapa' && !resultado.find((r) => r.shackKey === g.shackKey)) {
      resultado.push({ ...g, tipo: 'mapa' });
    }
  }

  // Restaurar certificados ocultos (visible === false) para preservar su posición y marco
  // aunque no estén en disponibles (shackVisible === false los excluye de la query)
  for (const g of guardados || []) {
    if (
      g.tipo === 'certificado' &&
      g.visible === false &&
      !resultado.find((r) => r.shackKey === g.shackKey)
    ) {
      resultado.push({ ...g });
    }
  }

  return resultado;
}

export function serializarItemsParaGuardar(items) {
  return (items || []).map((item) => ({
    shackKey: item.shackKey,
    tipo: item.tipo,
    wall: item.wall,
    u: round3(item.u),
    v: round3(item.v),
    w: round3(item.w),
    h: round3(item.h),
    ratio: round3(item.ratio || 1.5),
    frameTone: round3(item.frameTone ?? 0.60),
    zIndex: Number.isFinite(item.zIndex) ? item.zIndex : 0,
    visible: item.visible !== false,

    // encuadrador
    encuadrado:  item.encuadrado  ?? false,
    frameModel:  item.frameModel  ?? 'black',
    frameRatio:  item.frameRatio  ? round3(item.frameRatio) : null,
    frameRatioManual: item.frameRatioManual ?? false,
    imgRatio:    item.imgRatio    ? round3(item.imgRatio) : null,
    certState:   item.certState   ?? null,

    // campos de certificado
    storageType: item.storageType || 'user_drive',
    edicionId: item.edicionId || '',
    archivoId: item.archivoId || '',
    fileId:  item.fileId  || item.driveId  || '',
    fileUrl: item.fileUrl || item.driveUrl || '',
    concurso: item.concurso || '',
    anio: item.anio || '',
    modo: item.modo || '',
    nombre: item.nombre || '',

    // campos de mapa
    src: item.src || '',
    titulo: item.titulo || '',
    expandible: item.expandible !== false,
  }));
}

function round3(v) {
  return Math.round((Number(v) || 0) * 1000) / 1000;
}

// ── Vista pública ─────────────────────────────────────────────

// publicUrls: Map<shackKey, Firebase Storage URL pública>
export function sanitizarItemsPublicos(items, publicUrls = new Map()) {
  return (items || [])
    .filter((item) => item.visible !== false)
    .map((item) => {
      const base = {
        shackKey:         item.shackKey,
        tipo:             item.tipo,
        wall:             item.wall,
        u:                round3(item.u),
        v:                round3(item.v),
        w:                round3(item.w),
        h:                round3(item.h),
        ratio:            round3(item.ratio || 1.5),
        frameTone:        round3(item.frameTone ?? 0.60),
        zIndex:           Number.isFinite(item.zIndex) ? item.zIndex : 0,
        visible:          item.visible !== false,
        encuadrado:       item.encuadrado  ?? false,
        frameModel:       item.frameModel  ?? 'black',
        frameRatio:       item.frameRatio  ? round3(item.frameRatio) : null,
        frameRatioManual: item.frameRatioManual ?? false,
        imgRatio:         item.imgRatio    ? round3(item.imgRatio) : null,
        certState:        item.certState   ?? null,
      };

      if (item.tipo === 'certificado') {
        return {
          ...base,
          fileId:   item.shackKey,
          fileUrl:  publicUrls.get(item.shackKey) || '',
          concurso: item.concurso || '',
          anio:     item.anio || '',
          modo:     item.modo || '',
          nombre:   item.nombre || '',
        };
      }

      // mapa
      return {
        ...base,
        src:        item.src || '',
        titulo:     item.titulo || '',
        expandible: item.expandible !== false,
      };
    });
}

export async function guardarShackPublico(callsign, userId, items, publicUrls = new Map(), calibration = null) {
  // Defensa: si hay certificados visibles pero ninguna URL pública, abortar.
  // Escribir fileUrl:'' con merge:false borraría URLs válidas del documento.
  const certsVisibles = (items || []).filter(
    (i) => i.tipo === 'certificado' && i.visible !== false && i.fileId
  );
  if (certsVisibles.length > 0 && publicUrls.size === 0) {
    console.warn('[Shack] guardarShackPublico abortado: publicUrls vacío con certificados visibles.');
    return;
  }
  try {
    const docData = {
      userId,
      callsign,
      shackItems: sanitizarItemsPublicos(items, publicUrls),
      actualizadoEn: serverTimestamp(),
    };
    if (calibration !== null) {
      docData.calibration = calibration;
    }
    await setDoc(
      doc(db, 'publicShacks', callsign),
      docData,
      { merge: false }
    );
  } catch (error) {
    console.error('[Shack] Error al guardar shack público:', error);
    throw error;
  }
}

// Sube las imágenes visibles del shack a Firebase Storage (shack-public/{uid}/{shackKey}).
// thumbs: Map<fileId_real, blobUrl> — el mismo mapa que usa Shack v1 para renderizar.
// Retorna Map<shackKey, publicUrl>.
// Lanza si alguna subida falla.
export async function actualizarPreviewPublica(callsign, uid, previewBlob) {
  const storageRef = ref(storage, `shack-public/${uid}/shack-preview.png`);
  await uploadBytes(storageRef, previewBlob, { contentType: 'image/png' });
  const previewUrl = await getDownloadURL(storageRef);
  const previewVersion = Date.now();
  await setDoc(
    doc(db, 'publicShacks', callsign),
    { previewUrl, previewUpdatedAt: serverTimestamp(), previewVersion },
    { merge: true }
  );
  return { previewUrl, previewVersion };
}

export async function verificarShackPublicado(callsign) {
  const snap = await getDoc(doc(db, 'publicShacks', callsign));
  if (!snap.exists()) return null;
  const data = snap.data();
  return { previewUrl: data.previewUrl || null, previewVersion: data.previewVersion || null };
}

// Elimina archivos en shack-public/{uid}/ que ya no correspondan a ningún
// certificado visible actual. Nunca toca shack-preview.png ni archivos fuera
// de shack-public/{uid}/. Retorna el número de archivos eliminados.
// Llamar solo después de que publicShacks/{callsign} haya sido guardado con éxito.
export async function limpiarImagenesHuerfanas(uid, shackKeysActivos) {
  const keysNorm = new Set((shackKeysActivos || []).map((k) => k.replace(/:/g, '_')));
  const dirRef = ref(storage, `shack-public/${uid}`);
  const lista = await listAll(dirRef);
  const aEliminar = lista.items.filter((item) => {
    if (item.name === 'shack-preview.png') return false;
    const sinExt = item.name.replace(/\.[^.]+$/, '');
    return !keysNorm.has(sinExt);
  });
  await Promise.all(aEliminar.map((item) => deleteObject(item)));
  return aEliminar.length;
}

export async function subirImagenesPublicasShack(uid, items, thumbs) {
  const publicUrls = new Map();

  const certsVisibles = (items || []).filter(
    (item) => item.tipo === 'certificado' && item.visible !== false && item.fileId
  );

  for (const item of certsVisibles) {
    const blobUrl = thumbs?.get(item.fileId);
    if (!blobUrl) {
      console.warn(`[Shack] Sin thumbnail para ${item.shackKey} — no se sube imagen pública.`);
      continue;
    }

    try {
      const resp = await fetch(blobUrl);
      const blob = await resp.blob();

      const ext  = blob.type === 'image/png' ? 'png' : 'jpg';
      const nombre = item.shackKey.replace(/:/g, '_');
      const storageRef = ref(storage, `shack-public/${uid}/${nombre}.${ext}`);

      await uploadBytes(storageRef, blob, { contentType: blob.type });
      const url = await getDownloadURL(storageRef);

      publicUrls.set(item.shackKey, url);
      console.log(`[Shack] Imagen pública subida: ${item.shackKey} → ${url}`);
    } catch (err) {
      console.error(`[Shack] Error subiendo imagen pública ${item.shackKey}:`, err);
      throw err;
    }
  }

  return publicUrls;
}
